/** One locale's text. The stored `stringJson` is an array of these. */
export interface LocaleValue {
    locale: string;
    value: string;
}

export interface ContentStringCategory {
    id: string;
    name: string;
}

export interface ContentStringKey {
    id: string;
    key: string;
    categoryId: string;
    categoryName: string;
    /** The shipped text, as a JSON array of LocaleValue. Empty string when the key has no default. */
    defaultStringJson: string;
    appTypes: number[];
    /** Tokens the consuming app substitutes at render time, bare - "points", not "#points#". */
    placeholders: string[];
}

/** One brand's override of one key, as stored. */
export interface BrandContentString {
    brandId: string;
    contentStringKeyId: string;
    key: string;
    /** A JSON array of LocaleValue. */
    stringJson: string;
}

/** Where a cell's text comes from - drives how it renders. */
export type ContentStringOrigin = 'brand' | 'default' | 'fallback' | 'missing';

/** The locale every key is expected to define, and what the server falls back to. */
export const FALLBACK_LOCALE = 'en-US';

/**
 * Parses a stored `stringJson` array into a locale -> value lookup.
 *
 * Tolerant on purpose: this text is hand-edited, and one malformed row must not blank an entire screen.
 * A parse failure reads as "nothing defined", which is exactly how the server treats it too.
 */
export function parseLocaleValues(json: string | null | undefined): Record<string, string> {
    if (!json) return {};
    try {
        const parsed = JSON.parse(json);
        if (!Array.isArray(parsed)) return {};
        const out: Record<string, string> = {};
        for (const entry of parsed) {
            if (!entry || typeof entry.locale !== 'string' || typeof entry.value !== 'string') continue;
            out[entry.locale.trim().toLowerCase()] = entry.value;
        }
        return out;
    } catch {
        return {};
    }
}

/**
 * Builds the stored array from an edited map, dropping empties.
 *
 * Dropping empties is what makes "clear this field" mean "go back to the default" rather than "override the
 * default with nothing" - which would show a blank in the app, and look deliberate.
 */
export function toStringJson(values: Record<string, string>): string {
    const entries = Object.entries(values)
        .filter(([, value]) => value.trim().length > 0)
        .map(([locale, value]) => ({ locale, value }));
    return JSON.stringify(entries);
}

/**
 * Where the text for one locale comes from.
 *
 * 'fallback' is the one worth distinguishing: there is no text in THIS language, but the key has an en-US
 * default, so the app renders English rather than nothing. That is a translation gap, not a broken string,
 * and conflating it with 'missing' would tell someone their app is showing a key name when it is not.
 */
export function originFor(
    brandValues: Record<string, string>,
    defaultValues: Record<string, string>,
    locale: string,
): ContentStringOrigin {
    const key = locale.trim().toLowerCase();
    if (brandValues[key]?.trim()) return 'brand';
    if (defaultValues[key]?.trim()) return 'default';
    if (defaultValues[FALLBACK_LOCALE.toLowerCase()]?.trim()) return 'fallback';
    return 'missing';
}

/** The text a location in this locale would actually see, following the same chain the server uses. */
export function displayValueFor(
    brandValues: Record<string, string>,
    defaultValues: Record<string, string>,
    locale: string,
): string {
    const key = locale.trim().toLowerCase();
    if (brandValues[key]?.trim()) return brandValues[key];
    if (defaultValues[key]?.trim()) return defaultValues[key];
    return defaultValues[FALLBACK_LOCALE.toLowerCase()] ?? '';
}

/** The payload for creating or updating a key. `appTypes` replaces the target set wholesale. */
export interface ContentStringKeyInput {
    key: string;
    categoryId: string;
    defaultStringJson: string | null;
    appTypes: number[];
    /** Tokens the consuming app substitutes at render time, bare - "points", not "#points#". */
    placeholders: string[];
}

/**
 * How a placeholder is written.
 *
 * Mirrors `PlaceholderSyntax` in TouchTab.Skin. The delimiters have to be known on both sides — the server
 * stores names bare, and this is what turns one into text a client can put in a sentence — so the pair is
 * duplicated on purpose, and both sides have tests pinning it. Change one and the portal writes a token no
 * app will ever replace.
 *
 * Hashes rather than braces because the receipt service renders with Handlebars, and a `{{token}}` in
 * client-authored text would be swallowed by the template engine before the app saw it.
 */
export const placeholderToken = (name: string) => `#${name}#`;

// Letters, digits and underscore only. A lone '#' is ordinary restaurant copy — "table #4 is ready",
// "check #1207" — and a greedy match would treat the span between two of them as a token and flag
// perfectly good text.
const TOKEN_PATTERN = /#([A-Za-z0-9_]+)#/g;

/** Every token present in a piece of text, bare and lowercased, each reported once. */
export function findPlaceholders(text: string | null | undefined): string[] {
    if (!text) return [];

    const found = new Set<string>();
    for (const match of text.matchAll(TOKEN_PATTERN)) found.add(match[1].toLowerCase());
    return [...found];
}

/**
 * What is wrong with the tokens in a piece of text, if anything.
 *
 * The two problems are not equally serious, and the UI treats them differently:
 *
 * - `unknown` is a mistake. `#point#` for `#points#` renders literally to a customer, and nobody finds out
 *   until they ask. That blocks saving.
 * - `missing` is a choice. "Come back and claim your points" is legitimate wording that just doesn't want the
 *   number in it. Blocking that would be overruling a client on their own copy, so it only warns.
 */
export function checkPlaceholders(text: string | null | undefined, declared: string[]) {
    const allowed = new Set(declared.map(d => d.toLowerCase()));
    const used = findPlaceholders(text);

    return {
        unknown: used.filter(u => !allowed.has(u)),
        missing: [...allowed].filter(a => !used.includes(a)),
    };
}
